// İSG uzmanı portalı — tehlike kalemi skor alanları (SUNUM; oluştur editörü + kalem modalı ortak).
// İki 5x5 matris seçici: (1) **birincil risk** Olasılık × Şiddet, (2) **artık risk** (residual)
// önlem sonrası Olasılık × Şiddet. Her ikisi de canlı score + seviye rozetini MatrixSelect içinde
// gösterir (YALNIZ gösterim). Artık risk "Temizle" ile birlikte sıfırlanır (ikisi birlikte ya boş).
//
// **⛔ Sadece probability/severity taşınır (§19).** Bu bileşen score/level ÜRETMEZ/GÖNDERMEZ;
// değerleri yukarı (form) `onScore`/`onResidual` ile bildirir. Kalıcı band sunucudandır.

import { Button } from '@isgportal/shared';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MatrixSelect } from './MatrixSelect';

interface RiskItemScoreFieldsProps {
  probability: number | null;
  severity: number | null;
  onScore: (probability: number, severity: number) => void;
  residualProbability: number | null;
  residualSeverity: number | null;
  onResidual: (probability: number, severity: number) => void;
  onResidualClear: () => void;
  /** Aynı sayfada birden çok kalem için benzersiz a11y id öneki. */
  idPrefix: string;
  disabled?: boolean;
  /** Matris seçilmedi (score REQUIRED) hatası. */
  scoreError?: string;
  /** Residual tek taraflı (INCOMPLETE) hatası. */
  residualError?: string;
}

export function RiskItemScoreFields({
  probability,
  severity,
  onScore,
  residualProbability,
  residualSeverity,
  onResidual,
  onResidualClear,
  idPrefix,
  disabled,
  scoreError,
  residualError,
}: RiskItemScoreFieldsProps) {
  const { t } = useTranslation();
  const hasResidual = residualProbability != null || residualSeverity != null;

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {/* Birincil risk (zorunlu) */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-semibold text-fg">{t('risk.item.score.title')}</legend>
        <MatrixSelect
          idPrefix={`${idPrefix}-score`}
          probability={probability}
          severity={severity}
          onChange={onScore}
          disabled={disabled}
        />
        {scoreError ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {scoreError}
          </p>
        ) : null}
      </fieldset>

      {/* Artık risk (önlem sonrası — opsiyonel; ikisi birlikte ya boş) */}
      <fieldset className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <legend className="text-xs font-semibold text-fg">{t('risk.item.residual.title')}</legend>
          {hasResidual ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={onResidualClear}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              {t('risk.item.residual.clear')}
            </Button>
          ) : null}
        </div>
        <p className="-mt-1 text-xs text-muted">{t('risk.item.residual.hint')}</p>
        <MatrixSelect
          idPrefix={`${idPrefix}-residual`}
          probability={residualProbability}
          severity={residualSeverity}
          onChange={onResidual}
          disabled={disabled}
        />
        {residualError ? (
          <p role="alert" className="text-xs font-medium text-danger">
            {residualError}
          </p>
        ) : null}
      </fieldset>
    </div>
  );
}
